import {getBcp47LanguageTag, Locale} from '@aboutyou/config';
import {assert} from 'ts-essentials';

export const getFormattedTime = (date: Date, locale: Locale): string => {
  return date.toLocaleTimeString(getBcp47LanguageTag(locale), {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export type TimeFormat = Record<TimeUnit, string>;

export interface Time {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export type TimeUnit = keyof Time;

export type FlatTimeValuesWithRemaining = [string, string, string, string] & {
  remainingTime?: number;
};

const getRemainingTimeFromMilliseconds = (ms: number): Time => {
  let days: number | undefined;
  let hours: number | undefined;
  let minutes: number | undefined;
  let seconds: number | undefined;

  if (ms <= 0) {
    return {days: 0, hours: 0, minutes: 0, seconds: 0};
  }

  while (seconds === undefined) {
    if (days === undefined) {
      days = Math.floor(ms / msPerDay);
      ms -= msPerDay * days;
    }

    if (hours === undefined) {
      hours = Math.floor(ms / msPerHour);
      ms -= msPerHour * hours;
    }

    if (minutes === undefined) {
      minutes = Math.floor(ms / msPerMinute);
      ms -= msPerMinute * minutes;
    }

    if (seconds === undefined) {
      seconds = Math.floor(ms / msPerSecond);
    }
  }

  assert(days !== undefined);
  assert(hours !== undefined);
  assert(minutes !== undefined);

  return {
    days,
    hours,
    minutes,
    seconds,
  };
};

export const getRemainingTime = (start: Date, end: Date): Time => {
  return getRemainingTimeFromMilliseconds(end.getTime() - start.getTime());
};

const msPerSecond = 1000;
const msPerMinute = msPerSecond * 60;
const msPerHour = msPerMinute * 60;
const msPerDay = msPerHour * 24;

export const padZeroRemainingTime = (ms: number): TimeFormat => {
  const result = getRemainingTimeFromMilliseconds(ms);

  return Object.entries(result).reduce(
    (formattedResult, entry) => {
      const [key, value] = entry as [TimeUnit, number];

      formattedResult[key] = `${value.toString().padStart(2, '0')}`;

      return formattedResult;
    },
    {
      days: '',
      hours: '',
      minutes: '',
      seconds: '',
    },
  );
};

export const flattenPadZeroRemainingTime = (
  ms: number,
): FlatTimeValuesWithRemaining => {
  const remainingTime = padZeroRemainingTime(ms);
  const timeStrings = [
    remainingTime.days,
    remainingTime.hours,
    remainingTime.minutes,
    remainingTime.seconds,
  ] as FlatTimeValuesWithRemaining;

  timeStrings.remainingTime = ms;

  return timeStrings;
};
